// src/components/Login.jsx - Google sign-in (Firebase web popup) behind the landing page
import React, { useState } from 'react';
import { auth, db } from '../firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import api, { setAuthToken } from '../api';
import { GOOGLE_WEB_CLIENT_ID } from '../config/auth';
import logger from '../utils/logger';
import Landing from './Landing';
import { track } from '../utils/analytics';

const Login = ({ onLogin }) => {
  const [loading, setLoading] = useState(false);

  const handleGoogleLogin = async () => {
    setLoading(true);
    track('signin_click');

    try {
      logger.log('🔐 Starting Google sign-in...');
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ client_id: GOOGLE_WEB_CLIENT_ID });
      const firebaseResult = await signInWithPopup(auth, provider);
      const firebaseUser = firebaseResult.user;

      logger.log('🔍 Firebase user authenticated:', firebaseUser.uid);

      // Step 4: Fetch/Create user profile in Firestore
      const userDocRef = doc(db, 'users', firebaseUser.uid);
      const userDoc = await getDoc(userDocRef);

      let userData = {};
      if (userDoc.exists()) {
        // Existing user - get data and update login time
        userData = userDoc.data();
        await setDoc(userDocRef, {
          ...userData,
          lastLogin: new Date(),
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL
        }, { merge: true });

        logger.log('🔍 Existing user found. Premium status:', userData.isPremium);
      } else {
        // New user - create document
        userData = {
          email: firebaseUser.email,
          displayName: firebaseUser.displayName,
          photoURL: firebaseUser.photoURL,
          createdAt: new Date(),
          lastLogin: new Date(),
          isPremium: false,
          isAdmin: false,
          conversationHistory: [],
          profile: null
        };

        await setDoc(userDocRef, userData);
        logger.log('🔍 New user created');
      }

      // Step 5: Get JWT token from your backend
      logger.log('🔐 Requesting JWT token from backend...');
      try {
        // Get Firebase ID token
        const firebaseIdToken = await firebaseUser.getIdToken();

        // Exchange Firebase token for your JWT token
        const jwtResponse = await api.post('/v1/auth/exchange-token', {
          firebase_token: firebaseIdToken,
          user_id: firebaseUser.uid,
          user_data: {
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            isPremium: userData.isPremium || false,
            isAdmin: userData.isAdmin || false
          }
        });

        const jwtToken = jwtResponse.data.access_token;
        if (jwtToken) {
          // Store JWT token for future API calls
          setAuthToken(jwtToken);
          logger.log('🔐 JWT token stored successfully');
        } else {
          throw new Error('No JWT token received from backend');
        }
      } catch (jwtError) {
        console.error('🔐 JWT token exchange failed:', jwtError);
        // Without a JWT token the dashboard and chat APIs will reject every request.
        // Surface the error so the user can retry login instead of landing on a broken page.
        throw new Error(
          'Could not connect to server. Please check your internet connection and try again.'
        );
      }

      // Step 6: Build complete user object
      const completeUser = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        displayName: firebaseUser.displayName,
        photoURL: firebaseUser.photoURL,
        isPremium: userData.isPremium || false,
        isAdmin: userData.isAdmin || false
      };

      track('signin_ok', { returning: userDoc.exists() });
      logger.log('✅ Login successful:', {
        userId: completeUser.uid,
        isPremium: completeUser.isPremium,
        hasJWT: !!localStorage.getItem('jwt_token')
      });

      // Step 7: Pass complete user to parent component
      onLogin(completeUser);

    } catch (error) {
      console.error('❌ Login failed:', error);

      // Clear any stored tokens on error
      setAuthToken(null);

      let errorMessage = 'Login failed. Please try again.';

      if (error.message) {
        if (error.message.includes('network') || error.message.includes('Network')) {
          errorMessage = 'Network error. Please check your internet connection and try again.';
        } else if (error.message.includes('cancelled') || error.message.includes('canceled')) {
          // User cancelled - don't show error
          return;
        }
      }

      // Show better error UI instead of alert
      const errorDiv = document.createElement('div');
      errorDiv.style.cssText = `
        position: fixed; top: 20px; left: 50%; transform: translateX(-50%);
        background: #b3402f; color: white; padding: 12px 24px;
        border-radius: 2px; z-index: 10000; font-family: system-ui;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      `;
      errorDiv.textContent = errorMessage;
      document.body.appendChild(errorDiv);

      // Auto-remove after 5 seconds
      setTimeout(() => {
        if (errorDiv.parentNode) {
          errorDiv.parentNode.removeChild(errorDiv);
        }
      }, 5000);

      // Also add click to dismiss
      errorDiv.onclick = () => {
        if (errorDiv.parentNode) {
          errorDiv.parentNode.removeChild(errorDiv);
        }
      };
    } finally {
      setLoading(false);
    }
  };

  // Session-expired notice (set when api.js redirects here after a 401)
  const [showExpiredNote, setShowExpiredNote] = useState(
    () => new URLSearchParams(window.location.search).get('session') === 'expired'
  );

  return (
    <Landing
      onSignIn={handleGoogleLogin}
      signingIn={loading}
      sessionExpired={showExpiredNote}
      onDismissExpired={() => setShowExpiredNote(false)}
    />
  );
};

export default Login;
